import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPERATOR_FIELDS = [
  { name: "name", label: "Full Name", type: "text", required: true },
  { name: "brand_name", label: "Brand / Restaurant Name", type: "text", required: true },
  { name: "email", label: "Email Address", type: "email", required: true },
  { name: "phone", label: "Phone Number", type: "tel", required: false },
  { name: "target_market", label: "Target Metro Market", type: "text", required: false },
  { name: "launch_timeline", label: "Estimated Launch Timeline", type: "text", required: false },
];

const LANDLORD_FIELDS = [
  { name: "name", label: "Owner / Agent Name", type: "text", required: true },
  { name: "property_address", label: "Property Address", type: "text", required: true },
  { name: "property_type", label: "Property Type", type: "select", required: true, options: ["Gas Station", "Shopping Center", "Vacant Lot", "Parking Lot", "Other"] },
  { name: "available_sqft", label: "Available Sq. Footage / Parking Spaces", type: "text", required: false },
  { name: "email", label: "Contact Email", type: "email", required: true },
  { name: "phone", label: "Phone Number", type: "tel", required: false },
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const prefillSite = searchParams.get("site") || "";
  const initialTab = searchParams.get("tab") || (prefillSite ? "operator" : "operator");

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});

  useEffect(() => {
    const base = {};
    if (prefillSite) {
      base.site_name = prefillSite;
      base.message = `I'm interested in the "${prefillSite}" PadSite location.`;
    }
    setForm(base);
    setSubmitted(false);
  }, [activeTab, prefillSite]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        name: form.name || "",
        email: form.email || "",
        phone: form.phone || "",
        role: activeTab,
        message: Object.entries(form)
          .filter(([k]) => !["name", "email", "phone", "role", "site_name"].includes(k))
          .map(([k, v]) => `${k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}: ${v}`)
          .join("\n"),
        site_name: form.site_name || "",
      };
      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fields = activeTab === "operator" ? OPERATOR_FIELDS : LANDLORD_FIELDS;
  const title = activeTab === "operator" ? "Restaurant Operator Site Application" : "Landlord Property Submission";

  return (
    <div>
      <section data-testid="contact-hero" style={{ backgroundColor: "#2d2d2d" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pt-28 pb-14 md:py-16">
          <div className="max-w-3xl mb-8 md:mb-12">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-5 opacity-0 animate-fade-up" style={{ color: "#d4782f" }}>
              Get In Touch
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.08] mb-6 opacity-0 animate-fade-up animate-delay-1" style={{ color: "#FFFFFF" }}>
              {prefillSite ? `Request: ${prefillSite}` : "Let's Get Started."}
            </h1>
            <p className="text-base md:text-lg leading-relaxed opacity-0 animate-fade-up animate-delay-2" style={{ color: "rgba(255,255,255,0.7)" }}>
              {prefillSite
                ? "You're expressing interest in an available PadSite. Fill out the form below and we'll be in touch."
                : "Whether you're a restaurant operator looking for your next site or a property owner with available pad space, we'd like to hear from you."}
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex gap-0 mb-10 max-w-2xl opacity-0 animate-fade-up animate-delay-3">
            {[
              { key: "operator", label: "Operator Application" },
              { key: "landlord", label: "Landlord Submission" },
            ].map((tab) => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => { setActiveTab(tab.key); setSubmitted(false); setError(""); }}
                className="flex-1 px-4 py-3 text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.15em] font-bold transition-colors"
                style={{
                  backgroundColor: activeTab === tab.key ? "#d4782f" : "transparent",
                  color: activeTab === tab.key ? "#FFFFFF" : "rgba(255,255,255,0.5)",
                  borderBottom: activeTab === tab.key ? "none" : "1px solid rgba(212,120,47,0.3)",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form */}
          {submitted ? (
            <div data-testid="contact-success" className="border p-12 md:p-16 max-w-2xl opacity-0 animate-fade-up" style={{ borderColor: "#d4782f" }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: "#d4782f" }}>
                <Check size={24} style={{ color: "#FFFFFF" }} />
              </div>
              <h3 className="text-2xl font-bold tracking-tight mb-4" style={{ color: "#FFFFFF" }}>
                {activeTab === "operator" ? "Application Received." : "Property Submitted."}
              </h3>
              <p className="text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
                {activeTab === "operator"
                  ? "We've received your operator application. Our team will review your details and reach out within 48 hours."
                  : "We've received your property submission. Our site review team will evaluate your location and follow up shortly."}
              </p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={handleSubmit} className="max-w-2xl">
              <h3 className="text-lg font-bold mb-8" style={{ color: "#FFFFFF" }}>{title}</h3>

              {prefillSite && (
                <div className="mb-6 p-4 border" style={{ borderColor: "#d4782f", backgroundColor: "rgba(212,120,47,0.08)" }}>
                  <p className="text-xs uppercase tracking-[0.15em] font-semibold mb-1" style={{ color: "#d4782f" }}>Requesting Site</p>
                  <p className="text-base font-bold" style={{ color: "#FFFFFF" }}>{prefillSite}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {fields.map((field) => (
                  <div key={field.name} className={field.type === "select" ? "" : ""}>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold block mb-3" style={{ color: "rgba(255,255,255,0.5)" }}>
                      {field.label}{field.required && " *"}
                    </label>
                    {field.type === "select" ? (
                      <select
                        name={field.name}
                        data-testid={`contact-${field.name}`}
                        required={field.required}
                        value={form[field.name] || ""}
                        onChange={handleChange}
                        className="w-full px-5 py-4 text-base md:text-sm rounded-none border-none focus:outline-none appearance-none"
                        style={{ backgroundColor: "#FFFFFF", color: "#1A1D23" }}
                        onFocus={(e) => (e.target.style.boxShadow = "0 0 0 2px #d4782f")}
                        onBlur={(e) => (e.target.style.boxShadow = "none")}
                      >
                        <option value="">Select...</option>
                        {field.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        name={field.name}
                        data-testid={`contact-${field.name}`}
                        required={field.required}
                        value={form[field.name] || ""}
                        onChange={handleChange}
                        className="w-full px-5 py-4 text-base md:text-sm rounded-none border-none focus:outline-none"
                        style={{ backgroundColor: "#FFFFFF", color: "#1A1D23" }}
                        onFocus={(e) => (e.target.style.boxShadow = "0 0 0 2px #d4782f")}
                        onBlur={(e) => (e.target.style.boxShadow = "none")}
                      />
                    )}
                  </div>
                ))}
              </div>

              {error && <p data-testid="contact-error" className="text-sm mb-4" style={{ color: "#e74c3c" }}>{error}</p>}

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-8 py-4 font-bold tracking-wide transition-colors rounded-none text-sm disabled:opacity-50"
                style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
                onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = "#E08A45"; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = "#d4782f"; }}
              >
                {loading ? "Submitting..." : activeTab === "operator" ? "Submit Operator Application" : "Submit Property for Review"}
                <ArrowRight size={16} />
              </button>
            </form>
          )}

          {/* Phone Numbers */}
          <div className="mt-12 pt-8 border-t max-w-2xl" style={{ borderColor: "rgba(212,120,47,0.2)" }}>
            <p className="text-xs uppercase tracking-[0.15em] font-semibold mb-3" style={{ color: "#d4782f" }}>
              Prefer to call?
            </p>
            <p className="text-sm mb-1" style={{ color: "rgba(255,255,255,0.7)" }}>
              Toll-Free: <a href="tel:18884717237" className="inline-block py-3 font-bold" style={{ color: "#FFFFFF" }}>1-888-471-PADS (7237)</a>
            </p>
            <p className="text-sm mb-1" style={{ color: "rgba(255,255,255,0.7)" }}>
              Local: <a href="tel:17705890723" className="inline-block py-3 font-bold" style={{ color: "#FFFFFF" }}>770-589-0PAD (0723)</a>
            </p>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
              Email: <a href="mailto:info@padsitepartners.com" data-testid="contact-email-link" className="inline-block py-3 font-bold" style={{ color: "#FFFFFF" }}>info@padsitepartners.com</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
